import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaRegEdit } from "react-icons/fa";
import { MdDeleteForever } from "react-icons/md";
import { updateTasksApi, deleteTasksApi } from "../../Redux/Tasks/Task.actions";
import ConfirmModal from "../common/ConfirmModal";
import memberLabel from "../common/memberLabel";
import { projectOption } from "../common/projectLabel";
import activeProjects from "../common/activeProjects";
import AutocompleteSelect from "../common/AutocompleteSelect";
import Tooltip from "../common/Tooltip";
import padTimeString from "../common/padTimeString";
import { useI18n } from "../../i18n/I18nContext";
import { formatHMS } from "./reportFormat";
import styles from "./Styles/Reports.module.css";

const fieldsFromEntry = (e) => ({
  text: e.text,
  day: e.day,
  start: padTimeString(e.start) || "",
  end: padTimeString(e.end) || "",
  allDay: e.allDay,
  half: e.half,
  billable: e.billable !== false,
  clientId: e.clientId,
  projectId: e.projectId,
  userId: e.userId,
});

// How an entry was made, as admins see it in the row: typed-in and imported
// entries get a quiet tag; timer entries and ones made before the method was
// recorded get nothing.
const SOURCE_TAGS = {
  manual: { label: "reports.sourceManual", tooltip: "reports.sourceManualTooltip" },
  import: { label: "reports.sourceImport", tooltip: "reports.sourceImportTooltip" },
};

// One detailed-report row: read-only by default, switching to an inline
// edit form (reusing the same update/delete actions as the time tracker) so
// descriptions can be fixed or entries reassigned without leaving the report.
const ReportEntryRow = ({ entry, orgId, currency, isAdminOrOwner, showAmount, onChanged }) => {
  const { t } = useI18n();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { members } = useSelector((state) => state.organizations);
  const { clients } = useSelector((state) => state.clients);
  const { projects } = useSelector((state) => state.projects);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [form, setForm] = useState(fieldsFromEntry(entry));

  useEffect(() => {
    if (!isEditing) {
      setForm(fieldsFromEntry(entry));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, isEditing]);

  const canEditRow = isAdminOrOwner || entry.userId === user.id;

  // A single project/client picker (like the time record webview's) since
  // picking a project already determines its client.
  const handleProjectChange = (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    setForm((f) => ({ ...f, projectId, clientId: project ? project.clientId : f.clientId }));
  };

  const handleSave = () => {
    dispatch(
      updateTasksApi(
        {
          id: entry.id,
          clientId: form.clientId,
          projectId: form.projectId,
          userId: form.userId,
          text: form.text,
          day: form.day,
          allDay: form.allDay,
          half: form.half,
          billable: form.billable,
          start: form.allDay || form.half ? null : form.start,
          end: form.allDay || form.half ? null : form.end,
        },
        orgId,
        user.token
      )
    );
    setIsEditing(false);
    onChanged();
  };

  const handleDelete = () => {
    setShowDeleteConfirm(false);
    dispatch(deleteTasksApi(entry.id, orgId, user.token));
    onChanged();
  };

  if (isEditing) {
    return (
      <div className={styles.editRow}>
        <input
          className="cw-input"
          type="text"
          value={form.text}
          onChange={(e) => setForm({ ...form, text: e.target.value })}
          title={t("timeTracker.taskDescription")}
          autoFocus
        />
        <input
          className="cw-input"
          type="date"
          value={form.day}
          onChange={(e) => setForm({ ...form, day: e.target.value })}
          title={t("timeTracker.day")}
        />
        <label className={styles.allDayLabel}>
          <input
            type="checkbox"
            checked={form.allDay}
            onChange={(e) => setForm({ ...form, allDay: e.target.checked, half: e.target.checked ? false : form.half })}
          />{" "}
          {t("timeTracker.allDay")}
        </label>
        <label className={styles.allDayLabel}>
          <input
            type="checkbox"
            checked={form.half}
            onChange={(e) => setForm({ ...form, half: e.target.checked, allDay: e.target.checked ? false : form.allDay })}
          />{" "}
          {t("timeTracker.half")}
        </label>
        <label className={styles.allDayLabel} title={t("timeTracker.markBillable")}>
          <input
            type="checkbox"
            checked={form.billable}
            onChange={(e) => setForm({ ...form, billable: e.target.checked })}
          />{" "}
          {t("timeTracker.billable")}
        </label>
        {!form.allDay && !form.half && (
          <>
            <input
              className="cw-input"
              type="time"
              step="1"
              value={form.start}
              onChange={(e) => setForm({ ...form, start: e.target.value })}
              title={t("timeTracker.startTime")}
            />
            <input
              className="cw-input"
              type="time"
              step="1"
              value={form.end}
              onChange={(e) => setForm({ ...form, end: e.target.value })}
              title={t("timeTracker.endTime")}
            />
          </>
        )}
        {isAdminOrOwner && (
          <>
            <AutocompleteSelect
              label={t("projects.title")}
              placeholder={t("timeTracker.project")}
              options={activeProjects(projects, entry.projectId).map((p) => projectOption(p, clients))}
              value={form.projectId}
              onChange={handleProjectChange}
            />
            {/* Reassigning an entry only makes sense to someone who still
                works here, so former members are left out (the members list
                still carries the owner even when disabled, hence the filter). */}
            <AutocompleteSelect
              label={t("nav.users")}
              placeholder={t("timeTracker.searchMember")}
              options={members.filter((m) => !m.disabled).map((m) => ({ value: m.userId, label: memberLabel(m) }))}
              value={form.userId}
              onChange={(userId) => setForm((f) => ({ ...f, userId }))}
            />
          </>
        )}
        <div className={styles.editRowActions}>
          <button type="button" className="cw-button cw-button--sm" onClick={handleSave} title={t("common.saveChanges")}>
            {t("common.save")}
          </button>
          <button
            type="button"
            className="cw-button cw-button--secondary cw-button--sm"
            onClick={() => setIsEditing(false)}
            title={t("common.discardChanges")}
          >
            {t("common.cancel")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tableRow}>
      <span>{entry.day}</span>
      <span>
        {entry.text}
        <div className={styles.subText}>
          {entry.projectName} - {entry.clientName}
        </div>
      </span>
      <span>
        {padTimeString(entry.start) || "?"} - {padTimeString(entry.end) || "?"}
        {entry.allDay && ` (${t("timeTracker.allDay")})`}
        {entry.half && ` (${t("timeTracker.half")})`}
        {entry.billable === false && ` (${t("timeTracker.nonBillable")})`}
        {/* The API only sends the method to admins/owners; gated here too. */}
        {isAdminOrOwner && SOURCE_TAGS[entry.source] && (
          <Tooltip label={t(SOURCE_TAGS[entry.source].tooltip)}>
            <span className={styles.sourceTag}>{t(SOURCE_TAGS[entry.source].label)}</span>
          </Tooltip>
        )}
      </span>
      <span>{formatHMS(entry.durationSecs)}</span>
      <span>{entry.userName}</span>
      <span>{entry.userEmail}</span>
      {showAmount && (
        <span>
          {(entry.amount ?? 0).toFixed(2)} {currency}
        </span>
      )}
      <span className={styles.rowActions}>
        {canEditRow && (
          <>
            <Tooltip label={t("common.edit")}>
              <button type="button" className={styles.iconBtn} onClick={() => setIsEditing(true)}>
                <FaRegEdit />
              </button>
            </Tooltip>
            <Tooltip label={t("common.delete")}>
              <button type="button" className={styles.iconBtn} onClick={() => setShowDeleteConfirm(true)}>
                <MdDeleteForever />
              </button>
            </Tooltip>
          </>
        )}
      </span>

      <ConfirmModal
        show={showDeleteConfirm}
        title={t("timeTracker.deleteRecordTitle")}
        body={t("timeTracker.deleteRecordBody", { text: entry.text })}
        confirmLabel={t("common.delete")}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};

export default ReportEntryRow;
